import { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Loader2, Wrench, Plus, AlertTriangle, CheckCircle2, AlertCircle, Repeat, Camera, FileText, FilterX } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { RowDeleteButton } from '@/components/common/RowDeleteButton';
import { PageError } from '@/components/PageError';
import { apiClient, extractErrorMessage } from '@/lib/api';
import { UploadStatusRow } from '@/components/common/UploadStatusRow';
import type { UploadChipStatus } from '@/components/common/UploadStatusChip';

interface MEvent {
  id: string; code: string;
  vehicle_code: string | null; vehicle_plate: string | null;
  event_type: string; priority: string; status: string;
  reported_at: string;
  reporter_name: string | null; reporter_driver_name: string | null;
  total_cost_sar: string;
  photo_count: number; invoice_count: number;
  is_repeat_issue: boolean;
  // PR-CHIPS MAY 11
  uploadStatus?: { photos: UploadChipStatus; invoice: UploadChipStatus };
}
interface Summary {
  open_events: number; pending_verification: number; pending_approval: number; pending_payment: number;
  month_spend_sar: string; low_stock_count: number; service_due_today: number; repeat_this_week: number;
}

const STATUS_TONE: Record<string, string> = {
  reported: 'bg-muted text-muted-foreground',
  diagnosed: 'bg-sky-500/15 text-sky-700 dark:text-sky-400',
  in_progress: 'bg-amber-500/15 text-amber-700 dark:text-amber-400',
  work_complete: 'bg-amber-500/15 text-amber-700 dark:text-amber-400',
  pending_verification: 'bg-violet-500/15 text-violet-700 dark:text-violet-400',
  verified: 'bg-blue-500/15 text-blue-700 dark:text-blue-400',
  approved_for_payment: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400',
  paid: 'bg-emerald-700/15 text-emerald-800 dark:text-emerald-300',
  rejected: 'bg-rose-500/15 text-rose-700 dark:text-rose-400',
  cancelled: 'bg-rose-500/15 text-rose-700 dark:text-rose-400',
};

const PRIORITY_TONE: Record<string, string> = {
  low: 'text-muted-foreground',
  normal: '',
  high: 'text-amber-700 dark:text-amber-400 font-medium',
  urgent_breakdown: 'text-rose-700 dark:text-rose-400 font-bold',
};

const fmtSar = (s: string) => 'SAR ' + Number(s ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2 });

export default function MaintenanceListPage() {
  const { t } = useTranslation();
  const [items, setItems] = useState<MEvent[]>([]);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [priorityFilter, setPriorityFilter] = useState<string>('');
  // PR-CHIPS MAY 11
  const [missing, setMissing] = useState<string>('');
  const navigate = useNavigate();

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter)   params.set('status', statusFilter);
      if (priorityFilter) params.set('priority', priorityFilter);
      if (missing)        params.set('missing', missing);
      const [evs, sum] = await Promise.all([
        apiClient.get<MEvent[]>('/api/v1/maintenance/events?' + params.toString()),
        apiClient.get<Summary>('/api/v1/maintenance/dashboard-summary'),
      ]);
      setItems(Array.isArray(evs.data) ? evs.data : []);
      setSummary(sum.data);
      setError(null);
    } catch (e) { setError(extractErrorMessage(e)); }
    finally { setLoading(false); }
  }, [statusFilter, priorityFilter, missing]);

  useEffect(() => { refresh(); }, [refresh]);

  return (
    <div className="min-h-screen bg-background pb-24">
      <header className="border-b border-border bg-card sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between gap-2">
          <Link to="/dashboard"><Button variant="ghost" size="sm" className="gap-2"><ArrowLeft className="h-4 w-4" /> Dashboard</Button></Link>
          <div className="text-sm font-medium inline-flex items-center gap-2"><Wrench className="h-4 w-4" /> {t('maintenance.title', { defaultValue: 'Maintenance' })}</div>
          <Link to="/dashboard/maintenance/create"><Button size="sm" className="gap-1"><Plus className="h-4 w-4" /> New event</Button></Link>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-4 space-y-4">
        {summary && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
            <Stat label="Open events" value={String(summary.open_events)} />
            <Stat label="Pending verification" value={String(summary.pending_verification)} tone={summary.pending_verification > 0 ? 'warn' : undefined}
              link="/dashboard/maintenance/pending-verification" />
            <Stat label="Pending approval" value={String(summary.pending_approval)} tone={summary.pending_approval > 0 ? 'warn' : undefined}
              link="/dashboard/maintenance/pending-approval" />
            <Stat label="MTD spend" value={fmtSar(summary.month_spend_sar)} />
            <Stat label="Low stock" value={String(summary.low_stock_count)} tone={summary.low_stock_count > 0 ? 'warn' : undefined}
              link="/dashboard/maintenance/inventory?lowStock=true" />
          </div>
        )}

        <div className="flex items-center gap-2 flex-wrap">
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
            className="h-9 rounded-md border border-input bg-background px-3 text-sm">
            <option value="">All statuses</option>
            {Object.keys(STATUS_TONE).map(s => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
          </select>
          <select value={priorityFilter} onChange={(e) => setPriorityFilter(e.target.value)}
            className="h-9 rounded-md border border-input bg-background px-3 text-sm">
            <option value="">All priorities</option>
            {['low','normal','high','urgent_breakdown'].map(p => <option key={p} value={p}>{p.replace(/_/g, ' ')}</option>)}
          </select>
          {/* PR-CHIPS MAY 11 */}
          <select value={missing} onChange={(e) => setMissing(e.target.value)}
            className="h-9 rounded-md border border-input bg-background px-3 text-sm"
            title="Filter events by missing docs">
            <option value="">All docs</option>
            <option value="any">Missing any required</option>
            <option value="photos">Missing photos</option>
            <option value="invoice">Missing invoice</option>
          </select>
          {missing && (
            <Button variant="ghost" size="sm" className="gap-1" onClick={() => setMissing('')}>
              <FilterX className="h-3.5 w-3.5" /> Clear
            </Button>
          )}
          <Link to="/dashboard/maintenance/parts" className="text-xs text-muted-foreground underline ml-auto">Parts catalog</Link>
          <Link to="/dashboard/maintenance/inventory" className="text-xs text-muted-foreground underline">Inventory</Link>
          <Link to="/dashboard/maintenance/vendors" className="text-xs text-muted-foreground underline">Vendors</Link>
          <Link to="/dashboard/maintenance/schedule" className="text-xs text-muted-foreground underline">Schedule</Link>
          <Link to="/dashboard/maintenance/reports" className="text-xs text-muted-foreground underline">Reports</Link>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
        ) : error ? (
          <PageError error={error} retry={refresh} />
        ) : items.length === 0 ? (
          <div className="rounded-lg border border-border p-8 text-center text-sm text-muted-foreground">No maintenance events.</div>
        ) : (
          <div className="rounded-lg border border-border overflow-hidden">
            <table className="min-w-full text-sm">
              <thead className="text-xs text-muted-foreground bg-muted/30 border-b border-border">
                <tr>
                  <th className="text-left px-3 py-2">Code</th>
                  <th className="text-left px-3 py-2">Vehicle</th>
                  <th className="text-left px-3 py-2">Type</th>
                  <th className="text-left px-3 py-2">Priority</th>
                  <th className="text-left px-3 py-2">Status</th>
                  <th className="text-left px-3 py-2">Reported</th>
                  <th className="text-right px-3 py-2">Cost</th>
                  <th className="text-left px-3 py-2">DOCS</th>
                  <th className="text-center px-3 py-2">⟳</th>
                  <th className="text-end px-3 py-2 w-12" />
                </tr>
              </thead>
              <tbody>
                {items.map((e) => (
                  <tr key={e.id} className="border-b border-border last:border-b-0 hover:bg-muted/20">
                    <td className="px-3 py-2 font-mono text-xs"><Link to={`/dashboard/maintenance/${e.id}`} className="hover:underline">{e.code}</Link></td>
                    <td className="px-3 py-2 text-xs"><span className="font-mono">{e.vehicle_code}</span> · {e.vehicle_plate}</td>
                    <td className="px-3 py-2 text-xs">{e.event_type.replace(/_/g, ' ')}</td>
                    <td className={'px-3 py-2 text-xs ' + (PRIORITY_TONE[e.priority] ?? '')}>{e.priority.replace(/_/g, ' ')}</td>
                    <td className="px-3 py-2"><span className={'inline-flex items-center text-[10px] uppercase tracking-wider px-2 py-0.5 rounded ' + (STATUS_TONE[e.status] ?? '')}>{e.status.replace(/_/g, ' ')}</span></td>
                    <td className="px-3 py-2 text-xs text-muted-foreground">{new Date(e.reported_at).toLocaleDateString('en-GB')}</td>
                    <td className="px-3 py-2 text-right font-mono">{fmtSar(e.total_cost_sar)}</td>
                    <td className="px-3 py-2">
                      {e.uploadStatus ? (
                        <UploadStatusRow chips={[
                          { status: e.uploadStatus.photos,  icon: Camera,   label: 'Photos',
                            onClick: () => navigate(`/dashboard/maintenance/${e.id}?focus=photos`) },
                          { status: e.uploadStatus.invoice, icon: FileText, label: 'Invoice',
                            onClick: () => navigate(`/dashboard/maintenance/${e.id}?focus=invoice`) },
                        ]} />
                      ) : (
                        <>
                          {e.photo_count   > 0 ? <CheckCircle2 className="h-3 w-3 inline text-emerald-600" /> : <AlertCircle className="h-3 w-3 inline text-muted-foreground" />}
                          {e.invoice_count > 0 ? <CheckCircle2 className="h-3 w-3 inline text-emerald-600 ml-1" /> : <AlertCircle className="h-3 w-3 inline text-muted-foreground ml-1" />}
                        </>
                      )}
                    </td>
                    <td className="px-3 py-2 text-center text-xs">{e.is_repeat_issue ? <Repeat className="h-3 w-3 inline text-amber-600" /> : ''}</td>
                    <td className="px-3 py-2 text-end">
                      <RowDeleteButton
                        endpoint={`/api/v1/maintenance/events/${e.id}`}
                        entityKind="maintenance event"
                        label={e.code}
                        onDeleted={refresh}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
}

function Stat({ label, value, tone, link }: { label: string; value: string; tone?: 'good' | 'warn'; link?: string }) {
  const cls = tone === 'good' ? 'text-emerald-600 dark:text-emerald-400' : tone === 'warn' ? 'text-amber-600 dark:text-amber-400' : '';
  const inner = (
    <div className="bg-muted rounded-lg p-3">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={'text-xl font-medium mt-0.5 ' + cls}>{value}</div>
    </div>
  );
  return link ? <Link to={link}>{inner}</Link> : inner;
}
